import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  useMerchants, useCreateMerchant, useUpdateMerchant,
  useToggleMerchantStatus, useIndustries, useBusinessCategories, useTCSStaff,
  useDeleteMerchant, useResendInviteById
} from './hooks/useMerchants';
import { useSolutions } from '../metadata/hooks/useMetadata';
import { usePermissions, Permission } from '../../shared/hooks/usePermissions';
import { useProvisionInfra } from '../storefront-onboarding/hooks/useOnboarding';
import Button from '../../shared/components/Button';
import { AddressAutocomplete } from '../../shared/components/AddressAutocomplete';
import type { Merchant, CreateMerchantPayload, UpdateMerchantPayload } from './types';

// ─── Row actions dropdown ─────────────────────────────────────────────────────

interface RowActionsProps {
  merchant: Merchant;
  canEdit: boolean;
  canDeactivate: boolean;
  canDelete: boolean;
  canProvision: boolean;
  onEdit: () => void;
  onToggleStatus: () => void;
  onResendInvite: () => void;
  onDelete: () => void;
  isTogglingStatus: boolean;
  isResending: boolean;
  isDeleting: boolean;
}

function RowActions({
  merchant, canEdit, canDeactivate, canDelete, canProvision,
  onEdit, onToggleStatus, onResendInvite, onDelete,
  isTogglingStatus, isResending, isDeleting,
}: RowActionsProps) {
  const [open, setOpen] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [confirmProvision, setConfirmProvision] = useState(false);
  // Keyed by slug; dispatches the Terraform storefront-infra workflow for this merchant.
  const provisionInfra = useProvisionInfra(merchant.slug);
  const [menuPos, setMenuPos] = useState({ top: 0, left: 0 });
  const triggerRef = useRef<HTMLButtonElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const handler = (e: MouseEvent) => {
      if (
        menuRef.current && !menuRef.current.contains(e.target as Node) &&
        triggerRef.current && !triggerRef.current.contains(e.target as Node)
      ) {
        setOpen(false);
        setConfirmDelete(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  const close = () => { setOpen(false); setConfirmDelete(false); setConfirmProvision(false); };

  const handleOpen = () => {
    if (triggerRef.current) {
      const rect = triggerRef.current.getBoundingClientRect();
      setMenuPos({ top: rect.bottom + 4, left: rect.right - 192 });
    }
    setOpen((o) => !o);
    setConfirmDelete(false);
    setConfirmProvision(false);
  };

  const itemCls = 'w-full text-left px-4 py-2 text-sm hover:bg-muted transition-colors disabled:opacity-50 disabled:cursor-not-allowed';

  return (
    <div onClick={(e) => e.stopPropagation()}>
      <button
        ref={triggerRef}
        onClick={handleOpen}
        className="p-1.5 rounded-md hover:bg-muted text-foreground transition-colors"
        aria-label="Row actions"
      >
        <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
          <circle cx="12" cy="5" r="1.5" /><circle cx="12" cy="12" r="1.5" /><circle cx="12" cy="19" r="1.5" />
        </svg>
      </button>

      {open && (
        <div
          ref={menuRef}
          style={{ position: 'fixed', top: menuPos.top, left: menuPos.left, zIndex: 9999 }}
          className="w-48 bg-card border border-[#E5E7EB] rounded-[10px] shadow-lg overflow-hidden"
        >
          {canEdit && (
            <button className={itemCls} onClick={() => { onEdit(); close(); }}>
              Edit
            </button>
          )}
          {canDeactivate && (
            <button
              className={`${itemCls} ${merchant.active ? 'text-red-600' : 'text-green-700'}`}
              disabled={isTogglingStatus}
              onClick={() => { onToggleStatus(); close(); }}
            >
              {merchant.active ? 'Deactivate' : 'Activate'}
            </button>
          )}
          {canEdit && merchant.invitePending && (
            <button
              className={`${itemCls} text-amber-600`}
              disabled={isResending}
              onClick={() => { onResendInvite(); close(); }}
            >
              {isResending ? 'Sending…' : 'Resend Invite'}
            </button>
          )}
          {canProvision && (
            confirmProvision ? (
              <div className="px-4 py-2 space-y-1">
                <p className="text-xs text-foreground">Dispatch storefront infra workflow?</p>
                <div className="flex gap-2">
                  <button
                    disabled={provisionInfra.isPending}
                    onClick={() => { provisionInfra.mutate(); close(); }}
                    className="flex-1 py-1 text-xs font-semibold bg-foreground text-background rounded hover:opacity-90 disabled:opacity-50"
                  >
                    {provisionInfra.isPending ? '…' : 'Yes, provision'}
                  </button>
                  <button
                    onClick={() => setConfirmProvision(false)}
                    className="flex-1 py-1 text-xs border border-[#D9D9D9] rounded text-foreground hover:bg-muted"
                  >
                    Cancel
                  </button>
                </div>
              </div>
            ) : (
              <button
                className={itemCls}
                onClick={() => setConfirmProvision(true)}
              >
                Provision infra
              </button>
            )
          )}
          {canDelete && !merchant.active && (
            <>
              <div className="border-t border-[#E5E7EB]" />
              {confirmDelete ? (
                <div className="px-4 py-2 space-y-1">
                  <p className="text-xs text-foreground">Delete permanently?</p>
                  <div className="flex gap-2">
                    <button
                      disabled={isDeleting}
                      onClick={() => { onDelete(); close(); }}
                      className="flex-1 py-1 text-xs font-semibold bg-red-600 text-white rounded hover:bg-red-700 disabled:opacity-50"
                    >
                      {isDeleting ? '…' : 'Yes, delete'}
                    </button>
                    <button
                      onClick={() => setConfirmDelete(false)}
                      className="flex-1 py-1 text-xs border border-[#D9D9D9] rounded text-foreground hover:bg-muted"
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              ) : (
                <button
                  className={`${itemCls} text-red-600`}
                  onClick={() => setConfirmDelete(true)}
                >
                  Delete permanently
                </button>
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
}

function formatDate(dateStr: string | null) {
  if (!dateStr) return '—';
  return new Date(dateStr).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
}

function formatCommission(rate: number) {
  return `${(rate * 100).toFixed(1)}%`;
}

const inputCls = 'w-full bg-background text-foreground placeholder:text-muted-foreground border border-border rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-ring';
const labelCls = 'block text-sm font-medium text-foreground mb-1';
const optionalSpan = <span className="text-muted-foreground font-normal"> (optional)</span>;

function toggleId(list: string[], id: string) {
  return list.includes(id) ? list.filter((x) => x !== id) : [...list, id];
}

// ─── Step indicator ───────────────────────────────────────────────────────────

const CREATE_STEP_LABELS = ['Basics', 'Contract', 'Team', 'Admin'];
const EDIT_STEP_LABELS = ['Basics', 'Contract', 'Team'];

function StepIndicator({ current, labels }: { current: number; labels: string[] }) {
  return (
    <div className="flex items-center justify-between px-6 py-4 border-b bg-muted">
      {labels.map((label, i) => {
        const step = i + 1;
        const done = step < current;
        const active = step === current;
        return (
          <div key={step} className="flex items-center gap-2 flex-1">
            <div className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-semibold shrink-0 transition-colors ${
              done ? 'bg-black text-white' : active ? 'bg-[#28282A] text-white' : 'bg-muted text-muted-foreground'
            }`}>
              {done ? (
                <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
                  <path d="M2 6l3 3 5-5" stroke="white" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              ) : step}
            </div>
            <span className={`text-xs font-medium ${active ? 'text-foreground' : done ? 'text-muted-foreground' : 'text-muted-foreground'}`}>
              {label}
            </span>
            {i < labels.length - 1 && (
              <div className={`flex-1 h-px mx-2 ${done ? 'bg-black' : 'bg-muted'}`} />
            )}
          </div>
        );
      })}
    </div>
  );
}

// ─── Create wizard ────────────────────────────────────────────────────────────

interface WizardProps { onClose: () => void }

function CreateMerchantWizard({ onClose }: WizardProps) {
  const { data: industries = [] } = useIndustries();
  const { data: categories = [] } = useBusinessCategories();
  const { data: solutions = [] } = useSolutions();
  const { data: staff = [] } = useTCSStaff();
  const createMutation = useCreateMerchant();

  const [step, setStep] = useState(1);
  const [error, setError] = useState('');
  const [form, setForm] = useState({
    name: '',
    companyAddress: '',
    commissionCharge: '',
    solutionId: '',
    contractStartDate: '',
    contractEndDate: '',
    nextContractReviewDate: '',
    partnerSuccessBlueprintUrl: '',
    bpmOfficerId: '',
    industryIds: [] as string[],
    businessCategoryIds: [] as string[],
    adminFullName: '',
    adminEmail: '',
    phoneNumber: '',
  });

  const set = (key: keyof typeof form, val: string | string[]) =>
    setForm((f) => ({ ...f, [key]: val }));

  const validateStep = () => {
    setError('');
    if (step === 1) {
      if (!form.name.trim()) { setError('Merchant name is required'); return false; }
      if (!form.companyAddress.trim() || form.companyAddress.trim().length < 5) { setError('Company address must be at least 5 characters'); return false; }
      const c = parseFloat(form.commissionCharge);
      if (isNaN(c) || c < 0 || c > 100) { setError('Commission must be between 0 and 100'); return false; }
    }
    if (step === 2) {
      if (!form.contractStartDate) { setError('Contract start date is required'); return false; }
    }
    if (step === 3) {
      if (form.industryIds.length === 0) { setError('Select at least one industry'); return false; }
      if (form.businessCategoryIds.length === 0) { setError('Select at least one business category'); return false; }
    }
    if (step === 4) {
      if (!form.adminFullName.trim()) { setError('Admin full name is required'); return false; }
      if (!form.adminEmail.trim()) { setError('Admin email is required'); return false; }
    }
    return true;
  };

  const next = () => { if (validateStep()) setStep((s) => s + 1); };
  const back = () => { setError(''); setStep((s) => s - 1); };

  const handleSubmit = async (e: React.SyntheticEvent) => {
    e.preventDefault();
    if (!validateStep()) return;
    const commission = parseFloat(form.commissionCharge) / 100;
    const payload: CreateMerchantPayload = {
      name: form.name.trim(),
      companyAddress: form.companyAddress.trim(),
      commissionCharge: commission,
      contractStartDate: form.contractStartDate,
      industryIds: form.industryIds,
      businessCategoryIds: form.businessCategoryIds,
      adminEmail: form.adminEmail.trim(),
      adminFullName: form.adminFullName.trim(),
      ...(form.contractEndDate ? { contractEndDate: form.contractEndDate } : {}),
      ...(form.nextContractReviewDate ? { nextContractReviewDate: form.nextContractReviewDate } : {}),
      ...(form.partnerSuccessBlueprintUrl.trim() ? { partnerSuccessBlueprintUrl: form.partnerSuccessBlueprintUrl.trim() } : {}),
      ...(form.bpmOfficerId ? { bpmOfficerId: form.bpmOfficerId } : {}),
      ...(form.solutionId ? { solutionId: form.solutionId } : {}),
      ...(form.phoneNumber.trim() ? { phoneNumber: form.phoneNumber.trim() } : {}),
    };
    try {
      await createMutation.mutateAsync(payload);
      onClose();
    } catch (err: unknown) {
      setError((err as Error)?.message || 'Something went wrong');
    }
  };

  const isLoading = createMutation.isPending;

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
      <div className="bg-card rounded-lg w-full max-w-2xl max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="p-5 border-b flex items-center justify-between shrink-0">
          <h2 className="text-lg font-bold text-foreground">Create Merchant</h2>
          <button type="button" onClick={onClose} className="text-muted-foreground hover:text-muted-foreground">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M18 6L6 18M6 6l12 12" strokeLinecap="round" />
            </svg>
          </button>
        </div>

        {/* Step indicator */}
        <StepIndicator current={step} labels={CREATE_STEP_LABELS} />

        {/* Step body */}
        <form onSubmit={handleSubmit} className={`flex-1 p-5 space-y-4 ${step === 1 ? 'overflow-visible' : 'overflow-y-auto'}`}>

          {/* ── Step 1: Basics ───────────────────────────────────── */}
          {step === 1 && (
            <>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className={labelCls}>Merchant Name</label>
                  <input required className={inputCls} value={form.name} placeholder="e.g. Kente Collective"
                    onChange={(e) => set('name', e.target.value)} />
                </div>
                <div>
                  <label className={labelCls}>Commission Rate (%)</label>
                  <input required type="number" min="0" max="100" step="0.1" placeholder="e.g. 15"
                    className={inputCls} value={form.commissionCharge}
                    onChange={(e) => set('commissionCharge', e.target.value)} />
                </div>
              </div>
              <div>
                <label className={labelCls}>Solution</label>
                <select className={inputCls} value={form.solutionId} onChange={(e) => set('solutionId', e.target.value)}>
                  <option value="">Select a solution…</option>
                  {solutions.filter((s) => s.isActive).map((s) => (
                    <option key={s.slug} value={s.slug}>{s.name}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className={labelCls}>Company Address</label>
                <AddressAutocomplete
                  value={form.companyAddress}
                  onChange={(val) => set('companyAddress', val)}
                  onPlaceSelect={(data) => set('companyAddress', data.formattedAddress)}
                  placeholder="e.g. 123 Main St, London, UK"
                  disabled={isLoading}
                />
              </div>
            </>
          )}

          {/* ── Step 2: Contract ─────────────────────────────────── */}
          {step === 2 && (
            <>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className={labelCls}>Contract Start</label>
                  <input required type="date" className={inputCls} value={form.contractStartDate}
                    onChange={(e) => set('contractStartDate', e.target.value)} />
                </div>
                <div>
                  <label className={labelCls}>Contract End {optionalSpan}</label>
                  <input type="date" className={inputCls} value={form.contractEndDate}
                    onChange={(e) => set('contractEndDate', e.target.value)} />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className={labelCls}>Next Review Date {optionalSpan}</label>
                  <input type="date" className={inputCls} value={form.nextContractReviewDate}
                    onChange={(e) => set('nextContractReviewDate', e.target.value)} />
                </div>
              </div>
              <div>
                <label className={labelCls}>Partner Blueprint URL {optionalSpan}</label>
                <input type="url" placeholder="https://docs.example.com/blueprint" className={inputCls}
                  value={form.partnerSuccessBlueprintUrl}
                  onChange={(e) => set('partnerSuccessBlueprintUrl', e.target.value)} />
              </div>
            </>
          )}

          {/* ── Step 3: Team & Classification ────────────────────── */}
          {step === 3 && (
            <>
              <div>
                <label className={labelCls}>BPM Officer {optionalSpan}</label>
                <select className={inputCls} value={form.bpmOfficerId} onChange={(e) => set('bpmOfficerId', e.target.value)}>
                  <option value="">— Unassigned —</option>
                  {staff.map((u) => (
                    <option key={u.id} value={u.id}>{u.fullName}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className={labelCls}>Industries</label>
                <div className="flex flex-wrap gap-2">
                  {industries.filter((i) => i.isActive).map((industry) => (
                    <button key={industry.id} type="button"
                      onClick={() => set('industryIds', toggleId(form.industryIds, industry.id))}
                      className={`px-3 py-1 rounded-full text-sm border transition-colors ${
                        form.industryIds.includes(industry.id)
                          ? 'bg-black text-white border-black'
                          : 'bg-card text-foreground border-border hover:border-black'
                      }`}>
                      {industry.name}
                    </button>
                  ))}
                </div>
              </div>
              <div>
                <label className={labelCls}>Business Categories</label>
                <div className="flex flex-wrap gap-2">
                  {categories.filter((c) => c.isActive).map((category) => (
                    <button key={category.id} type="button"
                      onClick={() => set('businessCategoryIds', toggleId(form.businessCategoryIds, category.id))}
                      className={`px-3 py-1 rounded-full text-sm border transition-colors ${
                        form.businessCategoryIds.includes(category.id)
                          ? 'bg-black text-white border-black'
                          : 'bg-card text-foreground border-border hover:border-black'
                      }`}>
                      {category.name}
                    </button>
                  ))}
                </div>
              </div>
            </>
          )}

          {/* ── Step 4: Admin contact ─────────────────────────────── */}
          {step === 4 && (
            <>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className={labelCls}>Full Name</label>
                  <input required placeholder="e.g. Jane Doe" className={inputCls}
                    value={form.adminFullName} onChange={(e) => set('adminFullName', e.target.value)} />
                </div>
                <div>
                  <label className={labelCls}>Phone {optionalSpan}</label>
                  <input type="tel" placeholder="e.g. +44 7700 900000" className={inputCls}
                    value={form.phoneNumber} onChange={(e) => set('phoneNumber', e.target.value)} />
                </div>
              </div>
              <div>
                <label className={labelCls}>Email</label>
                <input required type="email" placeholder="e.g. jane@merchant.com" className={inputCls}
                  value={form.adminEmail} onChange={(e) => set('adminEmail', e.target.value)} />
              </div>
              <div className="rounded-md bg-blue-50 border border-blue-200 p-3 text-sm text-blue-700">
                An invite email will be sent to this address once you click <strong>Create Merchant</strong>.
                The link expires in 48 hours.
              </div>
            </>
          )}

          {error && <p className="text-red-500 text-sm">{error}</p>}
        </form>

        {/* Footer */}
        <div className="p-5 border-t flex items-center justify-between shrink-0">
          <button type="button" onClick={step === 1 ? onClose : back}
            className="px-4 py-2 text-sm font-medium text-foreground border border-border rounded-md hover:bg-muted">
            {step === 1 ? 'Cancel' : '← Back'}
          </button>
          <div className="text-xs text-muted-foreground">Step {step} of {CREATE_STEP_LABELS.length}</div>
          {step < CREATE_STEP_LABELS.length ? (
            <button type="button" onClick={next}
              className="px-4 py-2 text-sm font-semibold bg-black text-white rounded-md hover:bg-gray-800">
              Next →
            </button>
          ) : (
            <Button loading={isLoading} onClick={handleSubmit}>
              Create Merchant
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}

// ─── Edit wizard ──────────────────────────────────────────────────────────────

interface EditFormProps { merchant: Merchant; onClose: () => void }

export function EditMerchantForm({ merchant, onClose }: EditFormProps) {
  const { data: industries = [] } = useIndustries();
  const { data: categories = [] } = useBusinessCategories();
  const { data: tcsStaff = [] } = useTCSStaff();
  const { data: solutions = [] } = useSolutions();
  const updateMutation = useUpdateMerchant();

  const [step, setStep] = useState(1);
  const [error, setError] = useState('');
  const [form, setForm] = useState({
    name: merchant.name,
    companyAddress: merchant.companyAddress,
    commissionCharge: String(Math.round(merchant.commissionCharge * 100)),
    solutionId: merchant.solutionId,
    contractStartDate: merchant.contractStartDate.slice(0, 10),
    contractEndDate: merchant.contractEndDate?.slice(0, 10) ?? '',
    nextContractReviewDate: merchant.nextContractReviewDate?.slice(0, 10) ?? '',
    partnerSuccessBlueprintUrl: merchant.partnerSuccessBlueprintUrl ?? '',
    bpmOfficerId: merchant.bpmOfficerId ?? '',
    industryIds: merchant.industries.map((i) => i.industry.id),
    businessCategoryIds: merchant.businessCategories.map((c) => c.businessCategory.id),
  });

  const set = (key: keyof typeof form, val: string | string[]) =>
    setForm((f) => ({ ...f, [key]: val }));

  const validateStep = () => {
    setError('');
    if (step === 1) {
      if (!form.name.trim()) { setError('Merchant name is required'); return false; }
      if (form.companyAddress.trim().length < 5) { setError('Company address must be at least 5 characters'); return false; }
      const c = parseFloat(form.commissionCharge);
      if (isNaN(c) || c < 0 || c > 100) { setError('Commission must be between 0 and 100'); return false; }
    }
    if (step === 2) {
      if (!form.contractStartDate) { setError('Contract start date is required'); return false; }
    }
    if (step === 3) {
      if (form.industryIds.length === 0) { setError('Select at least one industry'); return false; }
      if (form.businessCategoryIds.length === 0) { setError('Select at least one business category'); return false; }
    }
    return true;
  };

  const next = () => { if (validateStep()) setStep((s) => s + 1); };
  const back = () => { setError(''); setStep((s) => s - 1); };

  const handleSubmit = async (e: React.SyntheticEvent) => {
    e.preventDefault();
    if (!validateStep()) return;
    const payload: UpdateMerchantPayload = {
      name: form.name.trim(),
      companyAddress: form.companyAddress.trim(),
      commissionCharge: parseFloat(form.commissionCharge) / 100,
      industryIds: form.industryIds,
      businessCategoryIds: form.businessCategoryIds,
      contractStartDate: form.contractStartDate,
      ...(form.contractEndDate ? { contractEndDate: form.contractEndDate } : { contractEndDate: undefined }),
      ...(form.nextContractReviewDate ? { nextContractReviewDate: form.nextContractReviewDate } : {}),
      ...(form.partnerSuccessBlueprintUrl.trim() ? { partnerSuccessBlueprintUrl: form.partnerSuccessBlueprintUrl.trim() } : {}),
      bpmOfficerId: form.bpmOfficerId || undefined,
      solutionId: form.solutionId || undefined,
    };
    try {
      await updateMutation.mutateAsync({ id: merchant.id, data: payload });
      onClose();
    } catch (err: unknown) {
      setError((err as Error)?.message || 'Something went wrong');
    }
  };

  const isLoading = updateMutation.isPending;

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
      <div className="bg-card rounded-lg w-full max-w-2xl max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="p-5 border-b flex items-center justify-between shrink-0">
          <h2 className="text-lg font-bold text-foreground">Edit Merchant</h2>
          <button type="button" onClick={onClose} className="text-muted-foreground hover:text-muted-foreground">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M18 6L6 18M6 6l12 12" strokeLinecap="round" />
            </svg>
          </button>
        </div>

        {/* Step indicator */}
        <StepIndicator current={step} labels={EDIT_STEP_LABELS} />

        {/* Step body */}
        <form onSubmit={handleSubmit} className={`flex-1 p-5 space-y-4 ${step === 1 ? 'overflow-visible' : 'overflow-y-auto'}`}>

          {/* ── Step 1: Basics ───────────────────────────────────── */}
          {step === 1 && (
            <>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className={labelCls}>Name</label>
                  <input required className={inputCls} value={form.name}
                    onChange={(e) => set('name', e.target.value)} />
                </div>
                <div>
                  <label className={labelCls}>Commission Rate (%)</label>
                  <input required type="number" min="0" max="100" step="0.1" placeholder="e.g. 15"
                    className={inputCls} value={form.commissionCharge}
                    onChange={(e) => set('commissionCharge', e.target.value)} />
                </div>
              </div>
              <div>
                <label className={labelCls}>Solution</label>
                <select className={inputCls} value={form.solutionId} onChange={(e) => set('solutionId', e.target.value)}>
                  {solutions.filter((s) => s.isActive).map((s) => (
                    <option key={s.slug} value={s.slug}>{s.name}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className={labelCls}>Company Address</label>
                <AddressAutocomplete
                  value={form.companyAddress}
                  onChange={(val) => set('companyAddress', val)}
                  onPlaceSelect={(data) => set('companyAddress', data.formattedAddress)}
                  placeholder="e.g. 123 Main St, London, UK"
                  disabled={isLoading}
                />
              </div>
            </>
          )}

          {/* ── Step 2: Contract ─────────────────────────────────── */}
          {step === 2 && (
            <>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className={labelCls}>Contract Start</label>
                  <input required type="date" className={inputCls} value={form.contractStartDate}
                    onChange={(e) => set('contractStartDate', e.target.value)} />
                </div>
                <div>
                  <label className={labelCls}>Contract End {optionalSpan}</label>
                  <input type="date" className={inputCls} value={form.contractEndDate}
                    onChange={(e) => set('contractEndDate', e.target.value)} />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className={labelCls}>Next Review Date {optionalSpan}</label>
                  <input type="date" className={inputCls} value={form.nextContractReviewDate}
                    onChange={(e) => set('nextContractReviewDate', e.target.value)} />
                </div>
                <div>
                  <label className={labelCls}>Partner Blueprint URL {optionalSpan}</label>
                  <input type="url" placeholder="https://…" className={inputCls}
                    value={form.partnerSuccessBlueprintUrl}
                    onChange={(e) => set('partnerSuccessBlueprintUrl', e.target.value)} />
                </div>
              </div>
            </>
          )}

          {/* ── Step 3: Team & Classification ────────────────────── */}
          {step === 3 && (
            <>
              <div>
                <label className={labelCls}>BPM Officer {optionalSpan}</label>
                <select className={inputCls} value={form.bpmOfficerId}
                  onChange={(e) => set('bpmOfficerId', e.target.value)}>
                  <option value="">— Unassigned —</option>
                  {tcsStaff.map((u) => (
                    <option key={u.id} value={u.id}>{u.fullName}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className={labelCls}>Industries</label>
                <div className="flex flex-wrap gap-2">
                  {industries.filter((i) => i.isActive).map((industry) => (
                    <button key={industry.id} type="button"
                      onClick={() => set('industryIds', toggleId(form.industryIds, industry.id))}
                      className={`px-3 py-1 rounded-full text-sm border transition-colors ${
                        form.industryIds.includes(industry.id)
                          ? 'bg-black text-white border-black'
                          : 'bg-card text-foreground border-border hover:border-black'
                      }`}>
                      {industry.name}
                    </button>
                  ))}
                </div>
              </div>
              <div>
                <label className={labelCls}>Business Categories</label>
                <div className="flex flex-wrap gap-2">
                  {categories.filter((c) => c.isActive).map((category) => (
                    <button key={category.id} type="button"
                      onClick={() => set('businessCategoryIds', toggleId(form.businessCategoryIds, category.id))}
                      className={`px-3 py-1 rounded-full text-sm border transition-colors ${
                        form.businessCategoryIds.includes(category.id)
                          ? 'bg-black text-white border-black'
                          : 'bg-card text-foreground border-border hover:border-black'
                      }`}>
                      {category.name}
                    </button>
                  ))}
                </div>
              </div>
            </>
          )}

          {error && <p className="text-red-500 text-sm">{error}</p>}
        </form>

        {/* Footer */}
        <div className="p-5 border-t flex items-center justify-between shrink-0">
          <button type="button" onClick={step === 1 ? onClose : back}
            className="px-4 py-2 text-sm font-medium text-foreground border border-border rounded-md hover:bg-muted">
            {step === 1 ? 'Cancel' : '← Back'}
          </button>
          <div className="text-xs text-muted-foreground">Step {step} of {EDIT_STEP_LABELS.length}</div>
          <div className="flex items-center gap-2">
            {step < EDIT_STEP_LABELS.length && (
              <button type="button" onClick={next}
                className="px-4 py-2 text-sm font-medium text-foreground border border-border rounded-md hover:bg-muted">
                Next →
              </button>
            )}
            <Button loading={isLoading} onClick={handleSubmit}>
              Save Changes
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── Main page ────────────────────────────────────────────────────────────────

export default function Merchants() {
  const navigate = useNavigate();
  const { data: merchants = [], isLoading, error } = useMerchants();
  const toggleStatus = useToggleMerchantStatus();
  const { hasPermission } = usePermissions();

  const [showCreate, setShowCreate] = useState(false);
  const [editingMerchant, setEditingMerchant] = useState<Merchant | undefined>();

  const deleteMerchant = useDeleteMerchant();
  const resendInvite = useResendInviteById();

  const [displayLimit, setDisplayLimit] = useState(15);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const sentinelRef = useRef<HTMLDivElement>(null);

  const visibleMerchants = merchants.slice(0, displayLimit);
  const hasMore = displayLimit < merchants.length;

  useEffect(() => { setDisplayLimit(15); }, [merchants.length]);

  useEffect(() => {
    if (!sentinelRef.current) return;
    const observer = new IntersectionObserver((entries) => {
      if (entries[0].isIntersecting && hasMore && !isLoadingMore) {
        setIsLoadingMore(true);
        setTimeout(() => { setDisplayLimit((p) => p + 15); setIsLoadingMore(false); }, 300);
      }
    }, { threshold: 0.1 });
    observer.observe(sentinelRef.current);
    return () => observer.disconnect();
  }, [hasMore, isLoadingMore]);

  const canCreate = hasPermission(Permission.MERCHANT_CREATE);
  const canEdit = hasPermission(Permission.MERCHANT_UPDATE);
  const canDeactivate = hasPermission(Permission.MERCHANT_DEACTIVATE);
  const canDelete = hasPermission(Permission.MERCHANT_DELETE);

  return (
    <div className="space-y-6 py-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-foreground">Merchants</h1>
          <p className="text-sm text-foreground mt-1">{merchants.length} merchant{merchants.length !== 1 ? 's' : ''}</p>
        </div>
        <div className="flex items-center gap-2">
          {canCreate && (
            <Button onClick={() => setShowCreate(true)}>Add Merchant</Button>
          )}
        </div>
      </div>

      {isLoading && <div className="text-center py-12 text-foreground">Loading merchants…</div>}
      {error && <div className="text-center py-12 text-red-500">Failed to load merchants.</div>}

      {!isLoading && !error && (
        <div className="bg-card rounded-lg border border-border overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-border bg-muted">
                  <th className="text-left px-4 py-3 font-semibold text-foreground">Name</th>
                  <th className="text-left px-4 py-3 font-semibold text-foreground">Industries</th>
                  <th className="text-left px-4 py-3 font-semibold text-foreground">Commission</th>
                  <th className="text-left px-4 py-3 font-semibold text-foreground">Contract Start</th>
                  <th className="text-left px-4 py-3 font-semibold text-foreground">BPM Officer</th>
                  <th className="text-left px-4 py-3 font-semibold text-foreground">Status</th>
                  <th className="px-4 py-3 w-10"></th>
                </tr>
              </thead>
              <tbody>
                {visibleMerchants.length === 0 ? (
                  <tr>
                    <td colSpan={7} className="text-center py-12 text-foreground">No merchants yet.</td>
                  </tr>
                ) : (
                  visibleMerchants.map((merchant) => (
                    <tr key={merchant.id}
                      onClick={() => navigate(`/merchants/${merchant.id}`)}
                      className="border-b border-border hover:bg-muted cursor-pointer">
                      <td className="px-4 py-3 font-medium text-foreground">{merchant.name}</td>
                      <td className="px-4 py-3 text-foreground">
                        {merchant.industries.map((i) => i.industry.name).join(', ') || '—'}
                      </td>
                      <td className="px-4 py-3 text-foreground">{formatCommission(merchant.commissionCharge)}</td>
                      <td className="px-4 py-3 text-foreground">{formatDate(merchant.contractStartDate)}</td>
                      <td className="px-4 py-3 text-foreground">{merchant.bpmOfficer?.fullName ?? '—'}</td>
                      <td className="px-4 py-3">
                        <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${
                          merchant.active ? 'bg-green-100 text-green-800' : 'bg-muted text-muted-foreground'
                        }`}>
                          {merchant.active ? 'Active' : 'Inactive'}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        <RowActions
                          merchant={merchant}
                          canEdit={canEdit}
                          canDeactivate={canDeactivate}
                          canDelete={canDelete}
                          canProvision={canCreate}
                          onEdit={() => setEditingMerchant(merchant)}
                          onToggleStatus={() => toggleStatus.mutate({ id: merchant.id, active: merchant.active })}
                          onResendInvite={() => resendInvite.mutate(merchant.id)}
                          onDelete={() => deleteMerchant.mutate(merchant.id)}
                          isTogglingStatus={toggleStatus.isPending}
                          isResending={resendInvite.isPending}
                          isDeleting={deleteMerchant.isPending}
                        />
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          <div ref={sentinelRef} className="flex justify-center py-6 border-t border-border">
            {isLoadingMore && (
              <div className="w-6 h-6 border-2 border-border border-t-gray-800 rounded-full animate-spin" />
            )}
            {!hasMore && merchants.length > 0 && (
              <span className="text-xs text-muted-foreground">Showing all {merchants.length} merchants</span>
            )}
          </div>
        </div>
      )}

      {showCreate && <CreateMerchantWizard onClose={() => setShowCreate(false)} />}
      {editingMerchant && (
        <EditMerchantForm merchant={editingMerchant} onClose={() => setEditingMerchant(undefined)} />
      )}
    </div>
  );
}
